import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '@/shared/lib/cn';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full border px-3 py-1 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1.5 [&>svg]:pointer-events-none transition-all duration-200 overflow-hidden',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary-surface text-primary dark:bg-primary/20 dark:text-primary-light',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        success: 'border-transparent bg-success/10 text-success border border-success/20',
        warning: 'border-transparent bg-warning/10 text-warning border border-warning/20',
        destructive:
          'border-transparent bg-destructive/10 text-destructive border border-destructive/20',
        outline: 'border-border text-foreground hover:bg-muted',
        premium:
          'border-transparent bg-gradient-to-r from-accent/20 to-accent-light/20 text-accent-dark border border-accent/30 font-semibold dark:text-accent-light',
        verified:
          'border-transparent bg-success/10 text-success border border-success/20 font-medium',
        online: 'border-transparent bg-success text-white font-medium',
        offline: 'border-transparent bg-muted text-muted-foreground font-medium',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
